import { useMemo, useState } from "react";
import StatusBadge from "@/components/base/StatusBadge";
import type { Appointment, ClientOption } from "./AppointmentModal";

/* ─── Types ──────────────────────────────────────────────────── */
interface Props {
  appointments: Appointment[];
  clients: ClientOption[];
  loading: boolean;
  apptTypes: string[];
  onEdit: (appt: Appointment) => void;
  onDelete: (appt: Appointment) => void;
  onQuickStatus: (appt: Appointment, next: string) => void;
  onNew: () => void;
}

/* ─── Status quick actions ───────────────────────────────────── */
const STATUS_QUICK_ACTIONS: Record<string, { label: string; next: string; icon: string; cls: string }[]> = {
  scheduled: [
    { label: "Confirm",    next: "confirmed", icon: "ri-checkbox-circle-line", cls: "text-emerald-500 hover:bg-emerald-50" },
    { label: "Cancel",     next: "cancelled", icon: "ri-close-circle-line",    cls: "text-red-400 hover:bg-red-50" },
  ],
  confirmed: [
    { label: "Complete",   next: "completed", icon: "ri-check-double-line",    cls: "text-[#C8A96E] hover:bg-amber-50" },
    { label: "No Show",    next: "no_show",   icon: "ri-user-unfollow-line",   cls: "text-orange-500 hover:bg-orange-50" },
    { label: "Cancel",     next: "cancelled", icon: "ri-close-circle-line",    cls: "text-red-400 hover:bg-red-50" },
  ],
  completed: [],
  cancelled:  [{ label: "Reschedule", next: "scheduled", icon: "ri-calendar-line", cls: "text-sky-500 hover:bg-sky-50" }],
  no_show:    [{ label: "Reschedule", next: "scheduled", icon: "ri-calendar-line", cls: "text-sky-500 hover:bg-sky-50" }],
};

const STATUS_TABS = [
  { value: "all",       label: "All" },
  { value: "scheduled", label: "Scheduled" },
  { value: "confirmed", label: "Confirmed" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "no_show",   label: "No Show" },
];

const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });

const fmtDuration = (start: string, end: string | null) => {
  if (!end) return null;
  const mins = Math.round((new Date(end).getTime() - new Date(start).getTime()) / 60000);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
};

/* ─── Component ──────────────────────────────────────────────── */
export default function AppointmentsListView({ appointments, clients, loading, apptTypes, onEdit, onDelete, onQuickStatus, onNew }: Props) {
  const [statusTab, setStatusTab]       = useState("all");
  const [typeFilter, setTypeFilter]     = useState("all");
  const [clientFilter, setClientFilter] = useState("all");
  const [dateFrom, setDateFrom]         = useState("");
  const [dateTo, setDateTo]             = useState("");
  const [search, setSearch]             = useState("");

  /* ── Counts ── */
  const counts = useMemo(() => {
    const c: Record<string, number> = { all: appointments.length };
    STATUS_TABS.slice(1).forEach((t) => {
      c[t.value] = appointments.filter((a) => a.status === t.value).length;
    });
    return c;
  }, [appointments]);

  /* ── Filtered ── */
  const filtered = useMemo(() => {
    return appointments.filter((a) => {
      if (statusTab !== "all" && a.status !== statusTab) return false;
      if (typeFilter !== "all" && a.appointment_type !== typeFilter) return false;
      if (clientFilter !== "all" && a.client_id !== clientFilter) return false;
      if (dateFrom && new Date(a.start_at) < new Date(dateFrom)) return false;
      if (dateTo   && new Date(a.start_at) > new Date(dateTo + "T23:59:59")) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const cName = clients.find((c) => c.id === a.client_id)?.full_name ?? "";
        if (!a.title.toLowerCase().includes(q) && !cName.toLowerCase().includes(q) && !(a.location ?? "").toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [appointments, statusTab, typeFilter, clientFilter, dateFrom, dateTo, search, clients]);

  const clientName = (id: string | null) =>
    id ? (clients.find((c) => c.id === id)?.full_name ?? "—") : "—";

  const activeFiltersCount = [typeFilter !== "all", clientFilter !== "all", dateFrom !== "", dateTo !== ""].filter(Boolean).length;

  const clearFilters = () => {
    setTypeFilter("all"); setClientFilter("all"); setDateFrom(""); setDateTo(""); setSearch(""); setStatusTab("all");
  };

  return (
    <div>
      {/* ── Filters ── */}
      <div className="flex flex-col gap-3 mb-6">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 max-w-sm">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400">
              <i className="ri-search-line text-sm"></i>
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search title, client, location…"
              className="w-full bg-white border border-slate-200 rounded-lg pl-9 pr-4 py-2.5 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors"
            />
          </div>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-600 outline-none focus:border-[#C8A96E]/50 cursor-pointer"
          >
            <option value="all">All Types</option>
            {apptTypes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>

          <select
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-600 outline-none cursor-pointer max-w-[180px]"
          >
            <option value="all">All Clients</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.full_name}</option>)}
          </select>

          <div className="flex items-center gap-1.5">
            <span className="text-slate-400 text-xs whitespace-nowrap">From</span>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-600 outline-none cursor-pointer" />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-slate-400 text-xs whitespace-nowrap">To</span>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-600 outline-none cursor-pointer" />
          </div>

          {(activeFiltersCount > 0 || search) && (
            <button onClick={clearFilters}
              className="flex items-center gap-1.5 px-3 py-2.5 text-sm text-red-400 bg-red-50 border border-red-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap hover:bg-red-100">
              <i className="ri-filter-off-line text-sm"></i>
              Clear
              {activeFiltersCount > 0 && <span className="bg-red-100 text-red-400 text-xs px-1.5 py-0.5 rounded-full">{activeFiltersCount}</span>}
            </button>
          )}
          <span className="text-slate-400 text-sm ml-auto whitespace-nowrap">{filtered.length} results</span>
        </div>

        {/* Status tabs */}
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1 flex-wrap">
          {STATUS_TABS.map((tab) => (
            <button key={tab.value} onClick={() => setStatusTab(tab.value)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                statusTab === tab.value ? "bg-[#C8A96E] text-white" : "text-slate-400 hover:text-[#0f172a]"
              }`}
            >
              {tab.label}
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${
                statusTab === tab.value ? "bg-white/25 text-white" : "bg-slate-100 text-slate-400"
              }`}>{counts[tab.value] ?? 0}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── List ── */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-6 h-6 border-2 border-[#C8A96E] border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-amber-50">
            <i className="ri-calendar-event-line text-[#C8A96E] text-3xl"></i>
          </div>
          <div>
            <p className="text-[#0f172a] font-semibold text-base mb-1">
              {appointments.length === 0 ? "No appointments yet" : "No appointments match filters"}
            </p>
            <p className="text-slate-400 text-sm">
              {appointments.length === 0 ? "Create your first appointment to start scheduling." : "Try adjusting the filters."}
            </p>
          </div>
          {appointments.length === 0 && (
            <button onClick={onNew}
              className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8975e] text-white text-sm font-semibold px-5 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap mt-1">
              <i className="ri-add-line"></i>Create First Appointment
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((appt) => {
            const quickActions = STATUS_QUICK_ACTIONS[appt.status] ?? [];
            const isPast = new Date(appt.start_at) < new Date();
            const duration = fmtDuration(appt.start_at, appt.end_at);
            const cName = clientName(appt.client_id);

            return (
              <div key={appt.id}
                className={`bg-white rounded-xl border transition-all ${isPast && appt.status === "scheduled" ? "border-amber-200" : "border-slate-200 hover:border-slate-300"}`}
              >
                <div className="flex items-center gap-4 px-5 py-4">
                  {/* Date block */}
                  <div className="w-12 shrink-0 text-center">
                    <p className="text-[#C8A96E] text-xs font-semibold uppercase">
                      {new Date(appt.start_at).toLocaleDateString("en-US", { month: "short" })}
                    </p>
                    <p className="text-[#0f172a] text-xl font-bold leading-none">{new Date(appt.start_at).getDate()}</p>
                    <p className="text-slate-400 text-[10px]">{fmtTime(appt.start_at)}</p>
                  </div>

                  <div className="w-px h-12 bg-slate-100 shrink-0" />

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <p className="text-[#0f172a] font-medium text-sm">{appt.title}</p>
                      {appt.appointment_type && (
                        <span className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full font-medium whitespace-nowrap">
                          {appt.appointment_type}
                        </span>
                      )}
                      {isPast && appt.status === "scheduled" && (
                        <span className="text-[10px] px-2 py-0.5 bg-amber-50 text-amber-500 border border-amber-200 rounded-full font-medium whitespace-nowrap">
                          <i className="ri-alarm-warning-line mr-1 text-[9px]"></i>Overdue
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      {cName !== "—" && (
                        <span className="text-slate-500 text-xs flex items-center gap-1">
                          <i className="ri-user-3-line text-[10px]"></i>{cName}
                        </span>
                      )}
                      {appt.location && (
                        <span className="text-slate-400 text-xs flex items-center gap-1">
                          <i className="ri-map-pin-line text-[10px]"></i>
                          <span className="truncate max-w-[160px]">{appt.location}</span>
                        </span>
                      )}
                      {duration && (
                        <span className="text-slate-400 text-xs flex items-center gap-1">
                          <i className="ri-time-line text-[10px]"></i>{duration}
                        </span>
                      )}
                      {appt.show_in_portal && (
                        <span className="text-slate-300 text-xs flex items-center gap-1">
                          <i className="ri-global-line text-[10px]"></i>Portal
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="shrink-0"><StatusBadge status={appt.status} /></div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 shrink-0">
                    {quickActions.map((qa) => (
                      <button key={qa.next} onClick={() => onQuickStatus(appt, qa.next)} title={qa.label}
                        className={`w-8 h-8 flex items-center justify-center text-slate-400 rounded-lg transition-all cursor-pointer ${qa.cls}`}>
                        <i className={`${qa.icon} text-sm`}></i>
                      </button>
                    ))}
                    <button onClick={() => onEdit(appt)} title="Edit"
                      className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#0f172a] hover:bg-slate-100 rounded-lg transition-all cursor-pointer">
                      <i className="ri-pencil-line text-sm"></i>
                    </button>
                    <button onClick={() => onDelete(appt)} title="Delete"
                      className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-red-400 hover:bg-red-50 rounded-lg transition-all cursor-pointer">
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                </div>

                {appt.notes && (
                  <div className="px-5 pb-3">
                    <p className="text-slate-400 text-xs italic border-t border-slate-100 pt-2 truncate">
                      <i className="ri-sticky-note-line mr-1"></i>{appt.notes}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}